import {
  Account_Read_Typeahead,
  getV1CompaniesAccountsTypeahead,
  GetV1CompaniesAccountsTypeaheadData,
} from '@treadinc/horizon-api-spec';
import { FieldValues } from 'react-hook-form';

import { AccountTypeahead } from '~hooks/useAccount/models';
import {
  ActingCompany,
  CONNECTED_VENDOR_ACCOUNTS_QUERY,
  connectedVendorCompanyOption,
  withOwnCompanyFirst,
} from '~utils/vendorCompanyOptions';

import AsyncAutocompleteField from '../v2/Autocomplete/AsyncAutocompleteField';
import { CommonAsyncAutocompleteFieldProps } from '../v2/Autocomplete/types';

type Props<T extends FieldValues> = CommonAsyncAutocompleteFieldProps<
  T,
  Account_Read_Typeahead,
  null,
  GetV1CompaniesAccountsTypeaheadData
> & { ownCompany: ActingCompany };

/**
 * Not `AutocompleteAccountsTypeahead`: drivers belong to companies, so an option is a vendor's
 * connected company, not its account. Options carry no `_entity`, because two vendor accounts
 * can share one connected company.
 */
export default function AutocompleteVendorCompaniesTypeahead<T extends FieldValues>({
  isDisabled,
  ownCompany,
  ...rest
}: Props<T>) {
  const ownCompanyOption = ownCompany.name
    ? { _entity: null, label: ownCompany.name, value: ownCompany.id }
    : undefined;

  return (
    <AsyncAutocompleteField
      {...rest}
      extraOptions={ownCompanyOption ? [ownCompanyOption] : []}
      filterOptions={(options, { inputValue }) =>
        withOwnCompanyFirst(
          options.filter((option) => option.value !== ownCompany.id),
          ownCompanyOption,
          inputValue,
        )
      }
      isDisabled={ownCompany.id ? isDisabled : true}
      queryName="AutocompleteVendorCompaniesTypeahead"
      fetchFn={{
        arguments: {
          path: { 'company-id': ownCompany.id },
          query: CONNECTED_VENDOR_ACCOUNTS_QUERY,
        },
        request: getV1CompaniesAccountsTypeahead,
      }}
      parseFn={(item: Account_Read_Typeahead) => {
        const option = connectedVendorCompanyOption(AccountTypeahead.parse(item));

        return option ? { _entity: null, ...option } : null;
      }}
    />
  );
}
